import type { MemberItem } from '@api';

import type { ManagedTeacherStatus, ManagedTeacherUser } from '../../models/managed-teacher-user';

/**
 * Maps a members API status string to the domain managed-teacher status.
 *
 * @param item - Raw member row from the API.
 * @returns Domain status for UI and store logic.
 */
export function mapMemberStatusToManagedTeacherStatus(item: MemberItem): ManagedTeacherStatus {
  switch (item.status) {
    case 'Active': {
      return 'Active';
    }
    case 'Deactivated': {
      return 'Inactive';
    }
    case 'Inactive': {
      return item.inviteSentAt == null ? 'Inactive' : 'Invited';
    }
    default: {
      return 'Inactive';
    }
  }
}

/**
 * Maps API member rows to domain managed-teacher users.
 *
 * @param items - Members from a list response.
 * @returns Domain users for the user-management store.
 */
export function memberItemsToManagedTeacherUsers(items: readonly MemberItem[]): ManagedTeacherUser[] {
  return items.map((item) => memberItemToManagedTeacherUser(item));
}

/**
 * Maps one API member row to a domain managed-teacher user.
 *
 * @param item - Raw member row from the API.
 * @returns Domain user model.
 */
export function memberItemToManagedTeacherUser(item: MemberItem): ManagedTeacherUser {
  const invitedAt = item.inviteSentAt ?? '';
  const inviteExpiresAt = item.inviteExpiresAt ?? '';

  return {
    id: item.id,
    firstName: item.firstName,
    lastName: item.lastName,
    email: item.email ?? '',
    role: 'Teacher',
    status: mapMemberStatusToManagedTeacherStatus(item),
    invitedAt,
    inviteExpiresAt,
    lastLoginAt: item.lastSignInAt,
    activityCardCount: 0,
    personalActivityCount: 0,
  };
}
